import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { ModalPreviewDocument } from "../../../../shared/components/ModalPreviewDocument/ModalPreviewDocument";
import { useToast } from "../../../../shared/hooks";
import { ISinisterCaseDocument, SinisterNegotiationService } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { ModalUploadCaseDocument } from "./ModalUploadCaseDocument";

interface Props {
	open: boolean;
	sinisterId: string;
	onClose: () => void;
}

export const ModalCaseDocumentList = ({ open, sinisterId, onClose }: Props) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
	const toastHandler = useToast();

	const [documents, setDocuments] = useState<ISinisterCaseDocument[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
	const [previewDoc, setPreviewDoc] = useState<ISinisterCaseDocument | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	const fetchDocuments = async () => {
		setIsLoading(true);
		const result = await SinisterNegotiationService.getCaseDocuments(sinisterId);
		if (!(result instanceof Error)) setDocuments(result);
		setIsLoading(false);
	};

	useEffect(() => {
		if (open) fetchDocuments();
	}, [open]);

	const handleClosePreview = () => {
		setPreviewDoc(null);
		setPreviewUrl(null);
	};

	const handlePreviewFile = async (doc: ISinisterCaseDocument) => {
		setIsLoading(true);
		const blob = await SinisterNegotiationService.previewCaseDocument(doc.id);
		if (blob instanceof Error) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro ao visualizar" content="Não foi possível abrir o documento." />,
			});
		} else {
			setPreviewDoc(doc);
			setPreviewUrl(URL.createObjectURL(blob));
		}
		setIsLoading(false);
	};

	const handleUploadFile = async (file: File) => {
		setIsLoading(true);
		const result = await SinisterNegotiationService.uploadCaseDocument(sinisterId, file);
		if (result instanceof Error) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro ao enviar documento" content="Não foi possível anexar o arquivo. Tente novamente." />,
			});
		} else {
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Documento enviado" content="O documento foi anexado com sucesso." />,
			});
			fetchDocuments();
		}
		setIsLoading(false);
	};

	const handleRemoveFile = async (doc: ISinisterCaseDocument) => {
		setIsLoading(true);
		const result = await SinisterNegotiationService.removeCaseDocument(doc.id);
		if (result instanceof Error) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro ao remover" content="Não foi possível remover o documento." />,
			});
		} else {
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Documento removido" content="O documento foi removido com sucesso." />,
			});
			fetchDocuments();
		}
		setIsLoading(false);
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h5" fontWeight={300} color="primary.main">
						Documentos da Cobrança
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2}>
						<Box display="flex" justifyContent="end" alignItems="center">
							<Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={() => setIsUploadModalOpen(true)}>
								Anexar Documento
							</Button>
						</Box>

						{documents?.length ? (
							<Stack spacing={2}>
								{documents.map((doc) => (
									<Paper key={doc.id} variant="outlined" sx={{ px: 2, py: 1 }}>
										<Stack direction="row" justifyContent="space-between" alignItems="center">
											<Stack direction="row" spacing={1} flex={1}>
												<InsertDriveFileIcon fontSize="small" sx={{ color: "#999" }} />
												<Stack spacing={0.5}>
													<Typography variant="body2" fontWeight={500} lineHeight={1.2}>
														{doc.originalFilename}
													</Typography>
													<Typography variant="caption" color="text.secondary" lineHeight={1}>
														{format(new Date(doc.uploadedDate), "dd/MM/yyyy HH:mm")}
													</Typography>
												</Stack>
											</Stack>
											<Stack direction="row" spacing={0.5}>
												<Tooltip title="Visualizar">
													<IconButton size="small" onClick={() => handlePreviewFile(doc)}>
														<VisibilityIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Baixar">
													<IconButton size="small" onClick={() => window.open(doc.url, "_blank", "noopener,noreferrer")}>
														<DownloadIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Remover">
													<IconButton size="small" onClick={() => handleRemoveFile(doc)}>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</Stack>
										</Stack>
									</Paper>
								))}
							</Stack>
						) : (
							<Typography variant="body2">Nenhum documento anexado.</Typography>
						)}
					</Stack>
				</DialogContent>
			</Dialog>

			{previewDoc && <ModalPreviewDocument open={!!previewDoc} onClose={handleClosePreview} previewDoc={previewDoc} previewUrl={previewUrl} isLoading={false} />}

			<ModalUploadCaseDocument
				isOpen={isUploadModalOpen}
				onClose={() => setIsUploadModalOpen(false)}
				onAttach={async (file) => {
					await handleUploadFile(file);
					setIsUploadModalOpen(false);
				}}
			/>

			<LoadingComp isopen={isLoading} />
		</>
	);
};
